// One shift, being written.
//
// Square's version of this warns "This team member is unavailable during this
// time" and offers Save anyway. That is the right shape — the barn knows things
// the schedule doesn't, and a manager who has already spoken to the person
// should not be stopped by software. So a conflict here is loud and never
// disabling.
//
// The conflict is worked out by the API, not here. Asking the server on every
// keystroke would be worse than useless, so this shows what is knowable from
// the week already loaded: time off, and the person's other shifts that day.
// The server checks again on save and is the one that decides.

import { useState } from 'react';
import TimeSelect from '../ui/TimeSelect';
import { colorClass } from '../../lib/lessonTypes';
import { personClass, assignPersonColors } from '../../lib/people';
import { defaultTimes, timeRange, minutesBetween, formatMinutes, timeOffOn } from '../../lib/shifts';
import { fmtDate } from '../../lib/dates';

/** "Sunday, Sep 6" — the long form the horse pages settled on. */
function longDay(iso) {
  return fmtDate(iso, 'dayMedium');
}

export default function ShiftEditor({
  shift, // null when adding
  date,
  userId, // the cell that was clicked; null means the open-shift row
  // { shiftTypeId, startTime, endTime } when this came from clicking a gap in
  // the day's plan. The routine already said what the work is and when.
  prefill = null,
  staff = [],
  types = [],
  timeOff = [],
  shifts = [],
  busy,
  error,
  onSave,
  onDelete,
  onClose,
}) {
  const editing = !!shift;

  // No kind of work until somebody picks one. It used to open on the first
  // type in the list, which made the field read as required — and the thing
  // most people want to write down first is simply the hours they'll be there.
  // A gap in the day's plan is the exception: the routine already said what
  // the work is.
  const [typeId, setTypeId] = useState(shift?.shiftTypeId || prefill?.shiftTypeId || '');
  const [who, setWho] = useState(shift ? shift.userId || '' : userId || '');
  // A routine's hours win over the shift type's defaults: a show week feeds at
  // five, and that is exactly the case a second routine exists for.
  const [start, setStart] = useState(
    shift?.startTime || prefill?.startTime || defaultTimes(null).startTime,
  );
  const [end, setEnd] = useState(
    shift?.endTime || prefill?.endTime || defaultTimes(null).endTime,
  );
  // Whether the hours are still the ones the form opened with. Once somebody
  // has typed 7 to 2, picking "Turnout" afterwards must not put them back to
  // Turnout's usual 7 to 10 — the hours were the point, the label is a detail.
  const [hoursTouched, setHoursTouched] = useState(!!(shift || prefill));
  const [notes, setNotes] = useState(shift?.notes || '');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Picking a kind of work fills in its usual hours — only when adding, only
  // when somebody actually picks, and only while the hours are still untouched.
  // This was an effect keyed on typeId, which also ran on mount and so
  // overwrote the hours a routine had just handed in.
  function chooseType(id) {
    setTypeId(id);
    if (editing || hoursTouched) return;
    const t = types.find((x) => x.id === id);
    if (!t) return;
    const d = defaultTimes(t);
    setStart(d.startTime);
    setEnd(d.endTime);
  }

  // The same hand-out the roster uses, so the stripe here is the card there.
  const colors = assignPersonColors([...staff.map((s) => s.userId), ...shifts.map((s) => s.userId)]);

  function changeStart(v) { setStart(v); setHoursTouched(true); }
  function changeEnd(v) { setEnd(v); setHoursTouched(true); }

  const minutes = minutesBetween(start, end);
  const backwards = start && end && minutes === 0;

  // What we can see from the week already in memory. The server checks again.
  const dayOff = who ? timeOffOn(timeOff, who, date) : [];
  // Mirrors the server's rule: a job inside somebody's "at the barn" hours is
  // not two places at once. Two jobs over each other are, and so are two
  // "at the barn" blocks.
  const clashes = who
    ? shifts.filter(
        (s) =>
          s.id !== shift?.id &&
          s.userId === who &&
          s.date === date &&
          start < s.endTime &&
          s.startTime < end &&
          !!s.shiftTypeId === !!typeId,
      )
    : [];

  const warnings = [
    ...dayOff.map((o) => ({
      tone: o.status === 'approved' ? 'blocking' : 'warning',
      text:
        o.status === 'approved'
          ? `${nameFor(staff, who)} has time off ${o.allDay ? 'all day' : timeRange(o.startTime, o.endTime)}${o.reason ? ` — ${o.reason}` : ''}.`
          : `${nameFor(staff, who)} has asked for this time off${o.reason ? ` — ${o.reason}` : ''}. It hasn't been decided.`,
    })),
    ...clashes.map((s) => ({
      tone: 'blocking',
      text: `Already on ${timeRange(s.startTime, s.endTime)}${s.typeName ? ` — ${s.typeName}` : ''}.`,
    })),
  ];

  function submit(e) {
    e.preventDefault();
    if (backwards) return;
    onSave({
      userId: who || null,
      shiftTypeId: typeId || null,
      date,
      startTime: start,
      endTime: end,
      notes: notes.trim() || null,
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-xl overflow-hidden max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={editing ? 'Edit shift' : 'Add shift'}
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-ink-100">
          <span
            className={'w-1 h-10 rounded-full shrink-0 mt-0.5 ' + (who ? personClass(who, 'spine', colors) : colorClass('slate', 'spine'))}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-lg leading-snug">{editing ? 'Edit shift' : 'Add shift'}</h2>
            <p className="text-sm text-ink-500">{longDay(date)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none shrink-0"
          >
            ×
          </button>
        </div>

        {warnings.length > 0 && (
          <div className="px-5 pt-4 space-y-2">
            {warnings.map((w, i) => (
              <p
                key={i}
                className={
                  'rounded-md px-3 py-2 text-sm ' +
                  (w.tone === 'blocking'
                    ? 'bg-rose-50 text-rose-800 border border-rose-200'
                    : 'bg-amber-50 text-amber-800 border border-amber-200')
                }
              >
                {w.text}
              </p>
            ))}
            {/* Said out loud rather than left for the person to infer from a
                button that still works. */}
            <p className="text-xs text-ink-400">You can save anyway.</p>
          </div>
        )}

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1" htmlFor="shift-who">
              Who
            </label>
            <select
              id="shift-who"
              className="input w-full"
              value={who}
              onChange={(e) => setWho(e.target.value)}
            >
              {/* An unstaffed shift is a real thing, not a blank field — it is
                  work the barn knows it needs and hasn't covered. */}
              <option value="">Open shift — nobody yet</option>
              {staff.map((s) => (
                <option key={s.userId} value={s.userId}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1" htmlFor="shift-type">
              Kind of work
            </label>
            <select
              id="shift-type"
              className="input w-full"
              value={typeId}
              onChange={(e) => chooseType(e.target.value)}
            >
              {/* The default, and the honest one: most of what gets written
                  down first is "I'm here 7 to 2", and what gets done in those
                  hours is decided later, if at all. */}
              <option value="">At the barn — no particular job</option>
              {/* A retired type is no longer offered, but a shift that already
                  carries one has to keep it: without this option the select
                  falls back to the first entry and saving quietly relabels
                  somebody's Tuesday. */}
              {shift?.shiftTypeId && !types.some((t) => t.id === shift.shiftTypeId) && (
                <option value={shift.shiftTypeId}>{shift.typeName || 'Retired type'} (retired)</option>
              )}
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="shift-start">Starts</label>
              <TimeSelect label="Start time" value={start} onChange={changeStart} id="shift-start" />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="shift-end">Ends</label>
              <TimeSelect label="End time" value={end} onChange={changeEnd} id="shift-end" />
            </div>
            <p className="text-sm text-ink-500 pb-2">
              {backwards ? (
                <span className="text-rose-600">End has to be after start.</span>
              ) : (
                formatMinutes(minutes)
              )}
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1" htmlFor="shift-notes">
              Notes
            </label>
            <input
              id="shift-notes"
              className="input w-full"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Vet here at 2, keep the aisle clear"
            />
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        {confirmingDelete ? (
          // Inline, replacing the footer, rather than a second dialog on top of
          // this one: the question is about the shift already on screen.
          <div className="px-5 py-4 border-t border-ink-100 bg-ink-50">
            <p className="text-sm text-ink-700">
              Delete this shift?{' '}
              {shift?.published ? (
                <>
                  It has been published, so <strong>{nameFor(staff, shift.userId) || 'whoever is on it'}</strong> will
                  be told straight away that it is canceled.
                </>
              ) : (
                'It was never published, so nobody has been told about it and nobody will be.'
              )}
            </p>
            <div className="mt-3 flex gap-2 justify-end">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmingDelete(false)}>
                Keep it
              </button>
              <button
                type="button"
                className="btn bg-rose-600 text-white hover:bg-rose-700"
                disabled={busy}
                onClick={() => onDelete(shift)}
              >
                Delete shift
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 border-t border-ink-100 flex items-center justify-between gap-2">
            {editing ? (
              <button
                type="button"
                className="text-sm text-rose-600 hover:text-rose-700"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy || backwards}>
                {busy ? 'Saving…' : editing ? 'Save shift' : 'Add shift'}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

function nameFor(staff, userId) {
  return staff.find((s) => s.userId === userId)?.name || '';
}

